import { inject, Inject, Injectable, InjectionToken } from '@angular/core';
import { L10n, L10nBase, QueryBus, RootContext, RootContextBase } from '@skylab-angular-shared/core';
import { Suz17AdapterServiceBase, Suz18ServiceBase, SuzCredentialsQuery, SuzInternalStatus, SuzService, withModule } from '@skylab-shared/business';
import { ConsoleLogger } from '@skylab-shared/core';

export const SUZ_SERVICE = new InjectionToken<SuzService>('SUZService', {
	providedIn: 'root',
	factory: () => {
		const factory = inject(SuzFactoryService);

		return new Proxy({} as SuzService, {
			get(_, prop) {
				return async (...args: any[]) => {
					const instance = await factory.init();
					const method = (instance as any)[prop];
					return method.apply(instance, args);
				};
			}
		});
	}
});

@Injectable({ providedIn: 'root'})
export class SuzFactoryService {
	#suzService: SuzService;
	#logger = new ConsoleLogger(this.constructor.name);

	public constructor(
		private readonly queryBus: QueryBus,
		@Inject(L10n) private readonly l10n: L10nBase,
		@Inject(RootContext) private readonly rootContext: RootContextBase,
	) {}

	public get suz(): SuzService {
		return this.#suzService;
	}

	public async init(): Promise<SuzService> {
		if (this.#suzService) return this.#suzService;
		const v17 = new Suz17AdapterServiceBase(this.l10n, this.rootContext);

		if (!withModule('Fiscalization')) {
			this.#suzService = v17;
			return this.#suzService;
		}

		try {
			const credentials = await this.queryBus.queryAsync(new SuzCredentialsQuery());
			const v18 = new Suz18ServiceBase(this.l10n, this.rootContext, credentials);

			const result = await v18.checkConnectionAsync();
			const connected = [SuzInternalStatus.Success, SuzInternalStatus.InvalidCredentials].includes(result.type);
			if (connected) {
				this.#suzService = v18;
			}
		} catch (err) {
			this.#logger.error(err);
		} finally {
			this.#suzService ??= v17;
			this.#logger.warn(`SELECTED SUZ VERSION: ${this.#suzService.version}`);
			return this.#suzService;
		}
	}
}
